import { ROOM_TYPES } from '../components/threadRoomType.ts';
import type { RoomType } from '../components/threadRoomType.ts';
import { normalizeUserType, USER_TYPES } from './userType.ts';

export const SCHOOL_ROLES = {
  PUPIL: 'pupil',
  CLASS_TEACHER: 'class-teacher',
  SAFEGUARDING_LEAD: 'safeguarding-lead',
  DEPUTY_LEAD: 'deputy-lead',
  PARENT: 'parent',
  SAFETY_REVIEWER: 'safety-reviewer'
} as const;

export type SchoolRole = typeof SCHOOL_ROLES[keyof typeof SCHOOL_ROLES];

export type SchoolClaims = {
  laboratory?: boolean;
  municipalityId?: string;
  schoolId?: string;
  schoolName?: string;
  classId?: string;
  yearGroup?: number | null;
  roles?: string[];
  householdId?: string;
};

const STAFF_ROLES = new Set<string>([
  SCHOOL_ROLES.CLASS_TEACHER,
  SCHOOL_ROLES.SAFEGUARDING_LEAD,
  SCHOOL_ROLES.DEPUTY_LEAD
]);

export function schoolClaimsFromUser(user: { schoolClaims?: SchoolClaims } | null | undefined): SchoolClaims {
  const claims = user && user.schoolClaims;
  if (!claims || typeof claims !== 'object') {
    return { laboratory: false, roles: [] };
  }
  return claims;
}

function rolesOf(user: { schoolClaims?: SchoolClaims } | null | undefined): string[] {
  const roles = schoolClaimsFromUser(user).roles;
  return Array.isArray(roles) ? roles : [];
}

export function isSchoolLaboratory(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return schoolClaimsFromUser(user).laboratory === true;
}

export function isPupil(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.PUPIL);
}

export function isSchoolStaff(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).some((role) => STAFF_ROLES.has(role));
}

export function isParent(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.PARENT);
}

export function isClassTeacher(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.CLASS_TEACHER);
}

export function isSafeguardingLead(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.SAFEGUARDING_LEAD);
}

export function isDeputyLead(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.DEPUTY_LEAD);
}

export function isSafetyReviewer(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return rolesOf(user).includes(SCHOOL_ROLES.SAFETY_REVIEWER);
}

export function canViewPastoralPrompts(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return isClassTeacher(user) || isParent(user) || isSafeguardingLead(user) || isDeputyLead(user);
}

export function canViewSafeguardingCases(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return isSafeguardingLead(user) || isDeputyLead(user);
}

export function canViewReviewQueue(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return isSafetyReviewer(user);
}

export function canCreateClassmate(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  const claims = schoolClaimsFromUser(user);
  const year = claims.yearGroup;
  return isPupil(user)
    && typeof year === 'number'
    && year >= 1
    && year <= 4
    && !!claims.schoolId;
}

export function canCreateStaff(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  const claims = schoolClaimsFromUser(user);
  if (!claims.schoolId || rolesOf(user).includes(SCHOOL_ROLES.SAFETY_REVIEWER)) return false;
  return isSchoolStaff(user) || isParent(user);
}

export function canCreateAdultPrivate(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return !isPupil(user);
}

export function canCreateFamily(user: { schoolClaims?: SchoolClaims; userType?: string } | null | undefined): boolean {
  return isPupil(user) || normalizeUserType(user && user.userType) === USER_TYPES.CHILD;
}

export function canCreateEncryptedChat(user: { schoolClaims?: SchoolClaims } | null | undefined): boolean {
  return canCreateAdultPrivate(user);
}

export function creatableRoomTypes(user: { schoolClaims?: SchoolClaims; userType?: string } | null | undefined): RoomType[] {
  const types: RoomType[] = [];
  if (canCreateClassmate(user)) types.push(ROOM_TYPES.SCHOOL_CLASSMATE);
  if (canCreateStaff(user)) types.push(ROOM_TYPES.SCHOOL_STAFF);
  if (canCreateFamily(user)) types.push(ROOM_TYPES.FAMILY);
  if (canCreateEncryptedChat(user)) types.push(ROOM_TYPES.ADULT_PRIVATE);
  return types;
}

export function defaultCreateRoomType(user: { schoolClaims?: SchoolClaims; userType?: string } | null | undefined): RoomType {
  const types = creatableRoomTypes(user);
  if (types.includes(ROOM_TYPES.ADULT_PRIVATE)) return ROOM_TYPES.ADULT_PRIVATE;
  if (types.includes(ROOM_TYPES.SCHOOL_CLASSMATE)) return ROOM_TYPES.SCHOOL_CLASSMATE;
  return types[0] || ROOM_TYPES.ADULT_PRIVATE;
}

export function showRoomTypePicker(user: { schoolClaims?: SchoolClaims; userType?: string } | null | undefined): boolean {
  return creatableRoomTypes(user).length > 1;
}

export function allowEmailLookupForRoom(roomType: string | undefined): boolean {
  return roomType !== 'school-classmate' && roomType !== 'school-staff';
}
